import { TRPCClientError } from '@trpc/client';
import { motion } from 'framer-motion';
import React, { useActionState, useState } from 'react';

import { isNotEmpty } from '@poseidon/components/common';
import { Button, ButtonLink, Input, Label } from '@poseidon/components/Input2';
import { Scrollable } from '@poseidon/components/Scrollable';
import { Arrows, Communication } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

import { api } from '@/utils/api';
import { LogoIcon } from '~/components/ToneIcons';
import { type NextPageWithLayout } from './_app';

type FormState = {
  errors?: {
    root?: string;
    email?: string;
  };
  email?: string;
};

const ForgetPage: NextPageWithLayout = () => {
  const utils = api.useUtils();
  const resetPasswordMutation = api.auth.resetPassword.useMutation({
    onSuccess: () => {
      void utils.user.me.invalidate();
    },
  });
  const [successMessageSend, setSuccessMessageSend] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | undefined>(undefined);

  const [state, submitAction, isPending] = useActionState<FormState | undefined, FormData>(async (_, formData) => {
    const emailValue = formData.get('email');
    const email = emailValue instanceof Object ? undefined : emailValue?.toString().trim();

    if (!isNotEmpty(email) || email.length <= 2) {
      return { errors: { email: 'Please provide an email' }, email };
    }

    try {
      await resetPasswordMutation.mutateAsync({ email });
      setSubmittedEmail(email);
      setSuccessMessageSend(true);
      return { email };
    } catch (error: unknown) {
      if (error instanceof TRPCClientError) {
        return { errors: { root: error.message || 'Failed to send email' }, email };
      }
      return { errors: { root: 'Failed to send email' }, email };
    }
  }, undefined);

  const openEmailApp = () => {
    window.open('https://mail.google.com/mail/u/0/#inbox', '_blank');
  };

  return (
    <CenteredAuthLayout>
      <StaticAuthCard>
        {!successMessageSend && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring' }}
            className="flex w-full flex-col items-center">
            <LogoIcon className="size-12 text-white" />
            <h2 className="mt-8 font-(family-name:--display-font) text-3xl font-bold tracking-tight text-white">
              Forgot password?
            </h2>
            <p className="my-4 mb-8 text-center text-base text-white/60">
              No worries, we&rsquo;ll send you reset instructions.
            </p>
            <form className="flex w-full flex-col items-stretch gap-4" action={submitAction}>
              <div className="flex flex-col gap-1">
                <Label className="text-white/80">Email</Label>
                <Input
                  name="email"
                  icon={<Communication.Mail01 />}
                  placeholder="Enter your email"
                  defaultValue={state?.email}
                  error={state?.errors?.email}
                  size="lg"
                  className="border-white/20! bg-white/10! text-white placeholder:text-white/40 focus-within:border-white/40! focus-within:bg-white/15! focus-within:outline-hidden! hover:bg-white/15!"
                />
              </div>

              {state?.errors?.root != null && <div className="text-fair-pink-600">{state.errors.root}</div>}

              <Button
                size="lg"
                type="submit"
                color="blue"
                textAlign="center"
                disabled={isPending}
                icon={isPending ? <WaveSpinner /> : undefined}>
                Reset password
              </Button>
              <ButtonLink
                size="lg"
                text
                href="/login"
                icon={<Arrows.ChevronLeft />}
                className="justify-center text-white/60 hover:bg-white/10! hover:text-white">
                Back to log in
              </ButtonLink>
            </form>
          </motion.div>
        )}
        {successMessageSend && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring' }}
            className="flex w-full flex-col items-center">
            <div className="flex size-12 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white">
              <Communication.Mail01 className="size-5" />
            </div>
            <h2 className="mt-8 font-(family-name:--display-font) text-3xl font-bold tracking-tight text-white">
              Check your email
            </h2>
            <p className="my-4 mb-8 text-center text-base text-white/60">
              We sent a password reset link to {submittedEmail}
            </p>
            <div className="flex w-full flex-col gap-4">
              <Button size="lg" type="button" color="blue" textAlign="center" onClick={openEmailApp}>
                Open email app
              </Button>
              <form action={submitAction}>
                <input type="hidden" name="email" value={submittedEmail ?? ''} />
                <div className="text-center text-sm text-white/60">
                  Didn&rsquo;t receive the email?
                  <Button type="submit" text className="ml-1 font-bold text-white hover:text-white/80">
                    Click to resend
                  </Button>
                </div>
              </form>
              <ButtonLink
                size="lg"
                text
                href="/login"
                icon={<Arrows.ChevronLeft />}
                className="justify-center text-white/60 hover:bg-white/10! hover:text-white">
                Back to log in
              </ButtonLink>
            </div>
          </motion.div>
        )}
      </StaticAuthCard>
    </CenteredAuthLayout>
  );
};

ForgetPage.layout = 'onlyIfNotLoggedIn';

export default ForgetPage;

function CenteredAuthLayout({ children }: { children: React.ReactNode }) {
  // Background set inline, not via a Tailwind arbitrary bg-url class: the compiled CSS is served from the
  // static-* CDN origin (assetPrefix), so a root-relative url() in CSS resolves against
  // the CDN bucket (404). Inline styles resolve against the page origin, which serves /public.
  return (
    <div
      className="relative flex h-screen w-screen items-center justify-center overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: "url('/onboarding.webp')" }}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative z-10 flex h-full w-full items-center justify-center">{children}</div>
    </div>
  );
}

function StaticAuthCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="auth-glass">
      <Scrollable
        className="h-screen lg:w-[480px]"
        innerClassName="flex min-h-full flex-col items-center justify-center border-x border-white/15 bg-white/8 px-10 py-8 shadow-2xl backdrop-blur-xl sm:px-14">
        {children}
      </Scrollable>
    </div>
  );
}
